import { getCoreRowModel, getSortedRowModel, type RowData, useReactTable } from '@tanstack/react-table';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';
import { TableV2 } from '~/components2/Table';
import { hasNoAssetsPresent, type UtilityAgency } from '~/pages/projectDetails/utilitiesV2/common';
import { isUnreliableProjectShape } from '~/pages/projectDetails/utilitiesV2/common/estimatedResponseTime';
import store from '../../../../store';
import { DrawerContainer, DrawerContent, DrawerFooter } from '../../../Drawer';
import { NextButton, PreviousButton } from '../../StepButtons';
import Constraints from './Constraints';
import { anyAgencyHasEstTime, columns, isMapsRequestable } from './columns';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData?: () => void;
  }
}

export default function SelectAssets() {
  const [data, setData] = useState<UtilityAgency[]>([]);
  // The flag mirror is read here so the step re-renders when PostHog resolves flags after the drawer opened.
  const { agencies, connectbaseCoverageEnabled, formState, project } = useSnapshot(store) as typeof store;
  // Gated behind PostHog (kept in sync with the utilities table's est-time gate). Also hide the whole
  // column when every agency would render a dash (no usable stats), or when the project shape makes
  // estimates unreliable (linear/large/scattered projects), per Shane's request.
  const estTimeFlag = useFeatureFlagEnabled('utilities-estimated-response-time');
  const hasEstTimeValues = useMemo(() => anyAgencyHasEstTime(data), [data]);
  // Memoized: the turf area/length walk can traverse thousands of vertices (KMZ imports), so only rerun
  // it when the project changes. Valtio keeps `project`'s reference stable across sort/expand/drawer
  // renders (those live in other stores), so this stays put until the project itself changes.
  const projectShapeUnreliable = useMemo(() => isUnreliableProjectShape(project), [project]);
  const showEstTime = estTimeFlag && hasEstTimeValues && !projectShapeUnreliable;
  const visibleColumns = useMemo(
    () =>
      showEstTime
        ? columns
        : columns.filter((c) => c.id !== 'estTime' && c.id !== 'estTimeDays' && c.id !== 'estTimeConfidence'),
    [showEstTime],
  );

  const refreshData = () => {
    setData([
      ...Object.values(agencies)
        .filter((a) => (isMapsRequestable(a) || a.asbuiltsAvailable) && !hasNoAssetsPresent(a))
        .sort((a, b) => a.agencyName.localeCompare(b.agencyName)),
    ]);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    refreshData();
  }, [agencies, formState, connectbaseCoverageEnabled]);

  const table = useReactTable({
    data,
    columns: visibleColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    getRowId: (row) => row.id,
    meta: { updateData: refreshData },
    initialState: {
      // Default order is by agency name; the Est time header cycles into the time / confidence sort modes.
      sorting: [{ id: 'agencyName', desc: false }],
      // Sort-only helper columns backing the Est time header's time / confidence sort modes; never rendered.
      columnVisibility: { estTimeDays: false, estTimeConfidence: false },
    },
  });

  return (
    <DrawerContainer>
      <DrawerContent>
        <TableV2 id="utilities-select-assets-v1" table={table} />
      </DrawerContent>

      <DrawerFooter>
        <div className="flex w-full flex-col">
          <Constraints />
          <div className="flex justify-between">
            <PreviousButton onClick={() => store.toggleFormStep(-1)} />

            <NextButton
              disabled={!formState.mapsSelection.size && !formState.asbuiltsSelection.size}
              onClick={() => {
                store.toggleFormStep(1);
              }}
            />
          </div>
        </div>
      </DrawerFooter>
    </DrawerContainer>
  );
}
